import type { Parser } from "../../tastyworks/deser"
import {
  EVENT_CONTRACT_CHAIN_DESER,
  EVENT_CONTRACT_CHAIN_LIST_DESER,
  EventContractChain,
  EventContractChainList,
} from "../../tastyworks/event-contract-chain"
import {
  EVENT_CONTRACT_INSTRUMENT_DESER,
  EventContractInstrument,
} from "../../tastyworks/event-contract-instrument"
import {
  EVENT_PRODUCT_DESER,
  EventProduct,
} from "../../tastyworks/event-product"
import {
  parseFuturesContract,
  type FuturesContract,
} from "../../tastyworks/futures-contract"
import type HttpClient from "../../tastyworks/http"
import {
  QUANTITY_DECIMAL_PRECISION_DESER,
  QuantityDecimalPrecision,
} from "../../tastyworks/quantity-decimal-precision"
import type { PaginationParams, Params } from "../../tastyworks/request"
import type { JsonMap } from "../../tastyworks/util/json"
import {
  parseItemsResponse,
  parseSingleResponse,
  type ItemsResponse,
  type SingleResponse,
} from "../../tastyworks/response"
import {
  INSTRUMENT_SEARCH_ITEM_DESER,
  InstrumentSearchItem,
} from "../../tastyworks/symbol-search-item"
import { fetchAllPages } from "../../tastyworks/util/pagination-request"

export interface InstrumentTypeGroup {
  "instrument-type": string
  symbols: string[]
}

export interface EventProductsParams extends Params {
  exchange: string
  frequency?: string
}

function encodeSymbol(symbol: string): string {
  return encodeURIComponent(symbol).replace(/\./g, "%2E")
}

const INSTRUMENTS_API_VERSION = "20260415"
export const INSTRUMENTS_API_HEADERS: HeadersInit = {
  "Accept-Version": INSTRUMENTS_API_VERSION,
}

export interface InstrumentSearchOptions {
  type?: string | null
  instrumentSubType?: string | null
  fromDate?: string | null
}

// NOTE: coincides with instruments-api max page size to utilize caching
const INSTRUMENTS_PER_PAGE_SIZE = 1000

export class InstrumentsService {
  constructor(private readonly instrumentsHttpClient: HttpClient) {}

  readonly getEventProducts = async (
    params: EventProductsParams
  ): Promise<ItemsResponse<EventProduct>> =>
    this.instrumentsHttpClient.indexSimple(
      EVENT_PRODUCT_DESER.toParser(EventProduct),
      "event-products",
      params,
      INSTRUMENTS_API_HEADERS
    )

  // A non-2xx answer with no JSON error body, like a gateway 404, parses as
  // zero precisions; rejecting it keeps the caller from caching that as the
  // real list. Java's client treats every non-2xx status as an error too.
  readonly getQuantityDecimalPrecisions = async (): Promise<
    ItemsResponse<QuantityDecimalPrecision>
  > => {
    const { helper, ok } = await this.instrumentsHttpClient.getWithStatus(
      "quantity-decimal-precisions",
      undefined,
      INSTRUMENTS_API_HEADERS
    )
    const response = parseItemsResponse(
      helper,
      QUANTITY_DECIMAL_PRECISION_DESER.toParser(QuantityDecimalPrecision)
    )
    if (!ok && !response.isError) {
      throw new Error("Quantity decimal precisions request failed")
    }

    return response
  }

  readonly getEventContractChain = async (
    symbol: string
  ): Promise<SingleResponse<EventContractChain>> => {
    const helper = await this.instrumentsHttpClient.get(
      `event-contract-chains/${symbol}/nested`,
      undefined,
      INSTRUMENTS_API_HEADERS
    )
    return parseSingleResponse(
      helper,
      EVENT_CONTRACT_CHAIN_DESER.toParser(EventContractChain)
    )
  }

  readonly getEventContractChains = async (
    exchange: string,
    underlyingProduct: string
  ): Promise<SingleResponse<EventContractChainList>> => {
    const helper = await this.instrumentsHttpClient.get(
      `event-contract-chains/${exchange}`,
      { "underlying-product": underlyingProduct },
      INSTRUMENTS_API_HEADERS
    )
    return parseSingleResponse(
      helper,
      EVENT_CONTRACT_CHAIN_LIST_DESER.toParser(EventContractChainList)
    )
  }

  readonly getEventContractChainsByProduct = async (
    exchange: string,
    eventProductExternalId: string
  ): Promise<SingleResponse<EventContractChainList>> => {
    const helper = await this.instrumentsHttpClient.get(
      `event-contract-chains/${exchange}/${encodeURIComponent(eventProductExternalId)}`,
      undefined,
      INSTRUMENTS_API_HEADERS
    )
    return parseSingleResponse(
      helper,
      EVENT_CONTRACT_CHAIN_LIST_DESER.toParser(EventContractChainList)
    )
  }

  readonly show = async (
    symbol: string
  ): Promise<SingleResponse<EventContractInstrument>> =>
    this.instrumentsHttpClient.show(
      new EventContractInstrument(),
      EVENT_CONTRACT_INSTRUMENT_DESER.update,
      `event-contracts/${encodeSymbol(symbol)}`,
      undefined,
      INSTRUMENTS_API_HEADERS
    )

  readonly fetchFuturesContractRecords = async (): Promise<{
    contracts: FuturesContract[]
    items: JsonMap[]
  }> => {
    const items: JsonMap[] = []
    const contracts = await fetchAllPages(
      async (params?: PaginationParams) =>
        this.instrumentsHttpClient.indexSimple(
          (helper) => {
            items.push(helper.toJson() as JsonMap)
            return parseFuturesContract(helper)
          },
          "futures",
          params,
          INSTRUMENTS_API_HEADERS
        ),
      { perPage: INSTRUMENTS_PER_PAGE_SIZE }
    )
    return { contracts, items }
  }

  readonly fetchFuturesContracts = async (): Promise<FuturesContract[]> =>
    (await this.fetchFuturesContractRecords()).contracts

  readonly searchInstruments = async (
    query: string,
    { type, instrumentSubType, fromDate }: InstrumentSearchOptions = {}
  ): Promise<ItemsResponse<InstrumentSearchItem>> =>
    this.instrumentsHttpClient.indexSimple(
      INSTRUMENT_SEARCH_ITEM_DESER.toParser(InstrumentSearchItem),
      "search",
      { query, type, instrumentSubType, fromDate: fromDate || undefined },
      INSTRUMENTS_API_HEADERS
    )

  readonly search = async <T>(
    groups: InstrumentTypeGroup[],
    parser: Parser<T>,
    options?: { fields?: Record<string, string> }
  ): Promise<ItemsResponse<T>> => {
    const totalSymbols = groups.reduce(
      (sum, group) => sum + group.symbols.length,
      0
    )
    const helper = await this.instrumentsHttpClient.postJson("search", {
      "page-offset": 0,
      "per-page": totalSymbols,
      "instrument-type-groups": groups,
      ...(options?.fields && { fields: options.fields }),
    })
    const unwrappedParser: Parser<T> = (itemHelper) => {
      const dataHelper = itemHelper.getChild("data")
      return parser(dataHelper)
    }
    return parseItemsResponse(helper, unwrappedParser)
  }
}
